import React, { useEffect, useRef, useState } from "react";
import { X, PhoneCall } from "lucide-react";
import { FAQ, GREETING, LEAD_STEPS, MASCOT_NAME } from "./mascotScript";
import { Bubble, BotLink, MascotAvatar, MascotHero, MascotLauncher } from "./MascotParts";
import { LeadForm } from "./LeadForm";

const greet = () => GREETING.map((t, i) => ({ id: `g${i}`, from: "bot", text: t }));
let seq = 0;

const Chips = ({ asked, onAsk, onLead }) => (
  <div className="flex flex-wrap gap-1.5 p-3 border-t border-slate-200 bg-white max-h-40 overflow-y-auto" data-testid="mascot-chips">
    {FAQ.filter((f) => !asked.includes(f.id)).map((f) => (
      <button key={f.id} type="button" onClick={() => onAsk(f)} data-testid={`mascot-q-${f.id}`}
        className="px-3 py-1.5 rounded-full border border-[#7c3aed]/40 text-[#5b21b6] bg-violet-50 hover:bg-violet-100 text-[12px] font-bold transition-colors">
        {f.q}
      </button>
    ))}
    <button type="button" onClick={onLead} data-testid="mascot-q-contato"
      className="px-3 py-1.5 rounded-full bg-[#7c3aed] text-white text-[12px] font-black inline-flex items-center gap-1 hover:bg-[#6d28d9] transition-colors">
      <PhoneCall className="w-3.5 h-3.5" /> Quero que entrem em contato
    </button>
  </div>
);

const Panel = ({ onClose }) => {
  const [msgs, setMsgs] = useState(greet);
  const [asked, setAsked] = useState([]);
  const [mode, setMode] = useState("chips");
  const [topic, setTopic] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const push = (from, text, link) => setMsgs((m) => [...m, { id: `m${++seq}`, from, text, link }]);
  const ask = (f) => {
    push("user", f.q);
    setAsked((a) => [...a, f.id]);
    setTopic((t) => (t ? `${t}, ${f.q}` : f.q));
    f.a.forEach((t, i) => setTimeout(() => push("bot", t, i === f.a.length - 1 ? f.link : null), 350 * (i + 1)));
  };
  const startLead = () => {
    push("user", "Quero que entrem em contato");
    setTimeout(() => push("bot", LEAD_STEPS[0].ask), 300);
    setMode("lead");
  };

  return (
    <div className="fixed z-50 bottom-0 right-0 sm:bottom-24 sm:right-5 w-full sm:w-[370px] h-[78dvh] sm:h-[560px] flex flex-col rounded-t-3xl sm:rounded-3xl overflow-hidden bg-slate-50 shadow-2xl shadow-indigo-950/30 border border-slate-200 mascot-panel-in" data-testid="mascot-chat-panel">
      <div className="flex items-center gap-3 px-4 py-3 bg-[#1e3a8a] text-white">
        <MascotAvatar size="w-11 h-11" />
        <div className="flex-1 min-w-0">
          <p className="font-heading font-black text-[15px] leading-tight">{MASCOT_NAME}</p>
          <p className="text-[11px] text-sky-200">Tira-dúvidas da AMC Escolas</p>
        </div>
        <button type="button" onClick={onClose} data-testid="mascot-chat-close" aria-label="Fechar" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="mascot-chat-messages">
        {msgs.length <= GREETING.length && <MascotHero />}
        {msgs.map((m) => (
          <Bubble key={m.id} from={m.from} testid={`mascot-msg-${m.from}`}>
            {m.text}{m.link && <><br /><BotLink link={m.link} /></>}
          </Bubble>
        ))}
        <div ref={end} />
      </div>
      {mode === "chips" && <Chips asked={asked} onAsk={ask} onLead={startLead} />}
      {mode === "lead" && <LeadForm topic={topic} onBot={(t) => push("bot", t)} onUser={(t) => push("user", t)} onDone={() => setMode("done")} />}
    </div>
  );
};

export default function MascotChat() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-mascot-chat", onOpen);
    return () => window.removeEventListener("open-mascot-chat", onOpen);
  }, []);

  if (open) return <Panel onClose={() => setOpen(false)} />;
  return <MascotLauncher onClick={() => setOpen(true)} testid="mascot-launcher" className="bottom-24 right-3" />;
}
